/**
 * Utility functions for file handling and MIME type detection
 */

export interface FileInfo {
    caseNumber: string
    fullName: string
    extension: string
}

/**
 * MIME type mapping for common file extensions
 */
export const MIME_TYPES: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.txt': 'text/plain',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.msg': 'application/vnd.ms-outlook',
    '.eml': 'message/rfc822',
    '.doc': 'application/msword',
    '.docx':
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx':
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.zip': 'application/zip',
    '.rar': 'application/x-rar-compressed',
    // fallback
    default: 'application/octet-stream',
}

/**
 * Get MIME type for a file extension
 */
export const getMimeType = (extension: string): string => {
    const normalizedExtension = extension.toLowerCase()
    return MIME_TYPES[normalizedExtension] || MIME_TYPES.default
}

/**
 * Check if a file should be downloaded instead of opened in browser
 */
export const shouldDownloadFile = (extension: string): boolean => {
    const normalizedExtension = extension.toLowerCase()
    const downloadExtensions = ['.msg', '.eml', '.zip', '.rar']

    // Download if it's a known download-only extension or unknown file type
    return (
        downloadExtensions.includes(normalizedExtension) ||
        !MIME_TYPES[normalizedExtension]
    )
}

/**
 * Create a blob from file data with appropriate MIME type
 */
export const createFileBlob = (
    fileData: ArrayBuffer,
    extension: string
): Blob => {
    const mimeType = getMimeType(extension)
    return new Blob([fileData], { type: mimeType })
}

/**
 * Download a file by creating a temporary link and clicking it
 */
export const downloadFile = (blob: Blob, fileName: string): void => {
    const fileURL = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = fileURL
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the object URL after download
    setTimeout(() => {
        URL.revokeObjectURL(fileURL)
    }, 100)
}

/**
 * Open a file in a new tab or same window based on screen size
 */
export const openFileInBrowser = (blob: Blob, isMobile = false): void => {
    const fileURL = URL.createObjectURL(blob)
    const target = isMobile ? '_self' : '_blank'
    window.open(fileURL, target)
}
